import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useCurrentUserHasFeatureFlag, useHasNotificationsConfiguration, useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { useIsCurrentUserAdmin } from "hooks/auth";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const isAdminUser = useIsCurrentUserAdmin();
	/*
	 * PRO-709: the whole Settings menu used to hinge on `useIsCurrentUserAdmin()`
	 * (`administers_company_rbac`), so a role holding matrix `settings = view` —
	 * Bidder Support, Bidder Lead, Procurement Lead — saw nothing at all rather
	 * than read-only access. Menu visibility now also accepts `settings` view,
	 * but the genuinely administrative children (account settings, user
	 * management, roles, audit) stay behind `isAdminUser`: "view settings" is not
	 * "administer the company".
	 *
	 * Writes on these pages are refused by the API (`company.member:settings,edit`
	 * on the RFx settings routes; CompanyPolicy::manageDesignSchema on the design
	 * schema). Showing the pages read-only is the agreed behaviour for this
	 * ticket; rendering their inputs inert is tracked separately.
	 */
	const { canView: canViewSettings } = useModuleCan("settings");
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isSupplierReportBrandingEnabled = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_REPORT_BRANDING_FOR_SUPPLIER);
	const hasNotificationsConfiguration = useHasNotificationsConfiguration();
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const usersLink = paths.accountSettings.users.list;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;
	const notificationSettingsLink = paths.notificationSettings;
	const rolesLink = paths.accountSettings.roles;
	const auditLink = paths.accountSettings.audit;

	const isAccountSettingsActive = currentPath.includes("account-settings") && !currentPath.includes("account-settings/users") && !currentPath.includes("account-settings/roles") && !currentPath.includes("account-settings/audit");
	const isUsersActive = currentPath.includes("account-settings/users");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");
	const isNotificationSettingsActive = currentPath.includes("settings/notifications");
	const isRolesActive = currentPath.includes("account-settings/roles");
	const isAuditActive = currentPath.includes("account-settings/audit");

	const showRfxSettings = (hasRfxSettings && isProcurer) || !isProcurer;
	const showPdfSettings = isSupplierReportBrandingEnabled;
	const hasChildren = showRfxSettings || isProcurer || isAdminUser;

	if (!isAdminUser && !canViewSettings) {
		return null;
	}

	// A settings-viewer who is not an admin has no administrative children, so the
	// menu would collapse to an empty shell pointing at a page they cannot open.
	if (!isAdminUser && !showRfxSettings && !showPdfSettings) {
		return null;
	}

	if (!hasChildren) {
		return (
			<AppMenuItem
				name="Settings"
				icon={<FiSettings size={24} />}
				link={accountSettingsLink}
				isCollapsed={isCollapsed}
				testId="account-settings"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
		);
	}

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={isAdminUser ? accountSettingsLink : (showRfxSettings ? rfxSettingsLink : pdfSettingsLink)}
			testId="settings-menu"
			onNavigate={onNavigate}
		>
			{isAdminUser && (
				<>
					<ExpandableMenuChildItem
						name="Account Settings"
						link={accountSettingsLink}
						isActive={isAccountSettingsActive}
						testId="account-settings-child"
						tourName="AccountSettings"
						onNavigate={onNavigate}
					/>
					<ExpandableMenuChildItem
						name="User Management"
						link={usersLink}
						isActive={isUsersActive}
						testId="users-child"
						onNavigate={onNavigate}
						tourName="UserManagement"
					/>
					<ExpandableMenuChildItem
						name="Roles & Permissions"
						link={rolesLink}
						isActive={isRolesActive}
						testId="roles-permissions-child"
						onNavigate={onNavigate}
					/>
					<ExpandableMenuChildItem
						name="Audit Trail"
						link={auditLink}
						isActive={isAuditActive}
						testId="audit-trail-child"
						onNavigate={onNavigate}
					/>
				</>
			)}
			{showRfxSettings && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{showPdfSettings && (
				<ExpandableMenuChildItem
					name="PDF Report Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{hasNotificationsConfiguration && (
				<ExpandableMenuChildItem
					name="Notification Settings"
					link={notificationSettingsLink}
					isActive={isNotificationSettingsActive}
					testId="notification-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
